import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
import { toast } from "react-toastify";
import {
  CONFIRM_PASSWORD_LABEL,
  CURRENT_PASSWORD_LABEL,
  EMPTY_STRING,
  NEW_PASSWORD_LABEL,
  PASSWORDS_MUST_MATCH,
  CHANGE_PASSWORD,
  CHANGE_PASSWORD_TITLE,
} from "Components/constants/commonNames";
import { REQUEST_CHANGE_PASSWORD_COMMON } from "API/Services/common";
import BaseButton from "../../Components/Common/BaseButton";
import BaseInput from "../../Components/Common/BaseInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  InputPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { checkStatusCodeSuccess } from "Components/constants/enums";

const validationSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .required(validationMessages.required(CURRENT_PASSWORD_LABEL))
    .min(8, validationMessages.minLength(CURRENT_PASSWORD_LABEL, 8)),
  newPassword: Yup.string()
    .required(validationMessages.required(NEW_PASSWORD_LABEL))
    .min(8, validationMessages.minLength(NEW_PASSWORD_LABEL, 8)),
  confirmPassword: Yup.string()
    .required(validationMessages.required(CONFIRM_PASSWORD_LABEL))
    .oneOf([Yup.ref("newPassword"), ""], PASSWORDS_MUST_MATCH),
});

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setLoader(true);
    const payload = { 
      oldPassword: values.currentPassword,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword
    };

    try {
      const resp: any = await REQUEST_CHANGE_PASSWORD_COMMON(payload);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        toast.success(resp.message);
        navigate("/login");
      } else {
        toast.error(resp.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setLoader(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    document.title = CHANGE_PASSWORD_TITLE;
  }, []);

  return (
    <div className="mt-5 p-5">
      <div className="auth-page-content">
        <Container className="mt-1">
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-2">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-info">{CHANGE_PASSWORD}</h5>
                  </div>

                  <div className="p-2 mt-3">
                    <form onSubmit={formik.handleSubmit}>
                      <BaseInput
                        label={CURRENT_PASSWORD_LABEL}
                        className="mb-3"
                        name="currentPassword"
                        type="password"
                        placeholder={InputPlaceHolder(CURRENT_PASSWORD_LABEL)}
                        value={formik.values.currentPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.currentPassword}
                        touched={formik.touched.currentPassword}
                        showPasswordToggle={true}
                      />
                      <BaseInput
                        label={NEW_PASSWORD_LABEL}
                        name="newPassword"
                        className="mb-3"
                        type="password"
                        placeholder={InputPlaceHolder(NEW_PASSWORD_LABEL)}
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.newPassword}
                        touched={formik.touched.newPassword}
                        showPasswordToggle={true}
                      />
                      <BaseInput
                        label={CONFIRM_PASSWORD_LABEL}
                        name="confirmPassword"
                        type="password"
                        className="mb-3"
                        placeholder={InputPlaceHolder(CONFIRM_PASSWORD_LABEL)}
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.confirmPassword}
                        touched={formik.touched.confirmPassword}
                        showPasswordToggle={true}
                      />
                      <div className="mt-4">
                        <BaseButton
                          text={loader ? EMPTY_STRING : CHANGE_PASSWORD}
                          loading={loader}
                          type="submit"
                          color="info"
                        />
                      </div>
                    </form>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default withRouter(ChangePasswordPage);
